import React from 'react';
import { createUseStyles } from 'react-jss';
import theme from '../../constants/Theme';
import { DiaryStatistics } from '../../types/diaryTypes';
import { Headline } from '../../elements';
import LoadingOverlay from '../LoadingOverlay/LoadingOverlay';
import DiaryChart from '../DiaryChart/DiaryChart';
import Emotion from '../Emotion/Emotion';
import { getRatingDescription, getTrendDescription } from '../../helpers/descriptionHelpers';

interface StatisticWidgetProps {
  diaryStatistics?: DiaryStatistics;
  isLoading?: boolean;
}

const StatisticWidget: React.FC<StatisticWidgetProps> = ({ diaryStatistics, isLoading = false }) => {
  const classes = useStyles();

  return (
    <div className={classes.container}>
      {isLoading && (
        <div className={classes.loadingContainer}>
          <LoadingOverlay isLoading={true} />
          <Headline fontSize={20} marginBottom={30}>
            Statistik
          </Headline>
        </div>
      )}
      {diaryStatistics && diaryStatistics.statistics.count <= 4 && (
        <div className={classes.emptyState}>
          <div className={classes.emptyStateMain}>
            <span className={classes.emptyCount}>{diaryStatistics.statistics.count}</span>
            <Headline fontSize={16} marginBottom={0}>
              Aktuelle Einträge
            </Headline>
          </div>
          <span className={classes.emptyStateDescription}>
            Es werden mindestens 5 Einträge im Hörtagebuch benötigt, um die Statistik freizuschalten.
          </span>
        </div>
      )}
      {diaryStatistics && diaryStatistics.statistics.count >= 5 && (
        <div className={classes.chartContainer}>
          <div className={classes.chartWrapper}>
            <div className={classes.chartOverview}>
              <div className={classes.chartEmotion}>
                <Emotion size={50} index={diaryStatistics.tendency.currentExperience} />
              </div>
              <div className={classes.chartDescription}>
                <span className={classes.currentExperience}>
                  {getRatingDescription(diaryStatistics.tendency.currentExperience)}
                </span>
                <span className={classes.trend}>{getTrendDescription(diaryStatistics.tendency.trend)}</span>
              </div>
            </div>
            <div className={classes.chart}>
              <DiaryChart history={diaryStatistics.history} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const useStyles = createUseStyles({
  container: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: theme.colors.contrastWhite,
    borderRadius: theme.borderRadius.card,
    boxShadow: theme.shadows.card,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 15,
    position: 'relative',
    height: '349px',
  },
  loadingContainer: {
    padding: 15,
    width: '100%',
    height: '100%',
    boxSizing: 'border-box',
  },
  emptyState: {
    padding: '38px 15px 38px 15px',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
  },
  emptyCount: {
    fontSize: 80,
    lineHeight: '100px',
    fontWeight: theme.typography.fontWeight.bold,
    color: theme.colors.blue,
  },
  emptyStateMain: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '60%',
  },
  emptyStateDescription: {
    fontSize: 16,
    lineHeight: '20px',
    color: theme.colors.blue,
    textAlign: 'center',
  },
  chartContainer: {
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    padding: 15,
    boxSizing: 'border-box',
    position: 'relative',
  },
  chartWrapper: {
    flex: 1,
    position: 'relative',
  },
  chart: { position: 'absolute', top: 30, left: 0, right: 0, bottom: 0 },
  chartOverview: {
    display: 'flex',
    alignItems: 'center',
  },
  chartEmotion: { paddingRight: 10 },
  chartDescription: { display: 'flex', flexDirection: 'column' },
  currentExperience: {
    fontWeight: theme.typography.fontWeight.bold,
    fontSize: 20,
    lineHeight: '24px',
    color: theme.colors.blue,
  },
  trend: { fontSize: 14, lineHeight: '18px', color: theme.colors.greyMain },
});

export default StatisticWidget;
